import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PageContainer from '../../shared/pageContainer';
import Search from '../../shared/search';
import CustomButton from '../../shared/customButton';
import Filters from '../../shared/filters';
import NotificationCard from '../../shared/notificationCard';
import GridTable from '../../shared/gridTable';
import StatusIndicator from '../../shared/statusIndicator';
import Tooltip from '../../shared/tooltip';
import UserAvatar from '../../shared/userAvatar';
import ActionButton from '../../shared/actionButton';
import AlertDialog from '../../shared/alertDialog';
import DotsMenu from '../../shared/dotsMenu';
import CustomFormDrawer from '../../shared/customFormDrawer';
import SelectField from '../../shared/selectField';
import AsyncListTooltip from '../../shared/asyncListTooltip';
import Avatar from '../../shared/avatar';
import PersonIcon from '../../../assets/icons/person.svg';
import {
  APP_PAGES,
  PARAMS,
  ABSENCE_REQUESTS_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { ABSENCE_REQUEST_STATUS_TYPES } from '../../../constants/statuses';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  INITIAL_ABSENCE_REQUESTS_SORT,
  ABSENCE_REQUEST_FIELD_NAMES,
  ABSENCE_FIELD_NAMES,
} from '../../../constants/absence';
import {
  API_ABSENCE_REQUESTS,
  API_ABSENCE_USERS_WITH_ABSENCES,
  API_ABSENCE_REQUESTS_OWN,
  api_absence_request_status,
  api_absence_request,
  api_absence_request_own,
  api_absence_request_delete_own,
  api_absence_request_actions,
} from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import { absenceRequestsSelector } from '../../../store/selectors/absenceRequestsSelector';
import { personAbsencesSelector } from '../../../store/selectors/personAbsencesSelector';
import {
  getAllAbsenceRequests,
  clearAllAbsenceRequests,
} from '../../../store/modules/absenceRequests';
import {
  getPersonAbsences,
  clearPersonAbsences,
} from '../../../store/modules/personAbsences';
import { useTranslations } from '../../../utility/useTranslations';
import http from '../../../utility/http';
import {
  prepareYearOptions,
  prepareTranslatedAbsenceRequestStatuses,
  prepareAbsenceRequestsPageFilters,
  prepareAbsenceRequestMenuItems,
  prepareUserAbsences,
  prepareManageAbsenceRequestInitialData,
  prepareManageAbsenceRequestFields,
  prepareWorkdays,
} from '../../../utility/absence';
import {
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  trimString,
  getItemById,
} from '../../../utility/helpers';
import {
  parseQueryParams,
  parseDuplicateParameters,
  hasNextPage,
  showSuccessMessage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import { formatDate } from '../../../utility/dateUtils';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import {
  prepareAbsenceRequestsPageTableActionsHeader,
  prepareAbsenceRequestsPageTableHeaders,
  prepareAbsenceRequestsTableData,
  prepareNoResultsLabels,
} from './config';

const useStyles = makeStyles(
  ({ breakpoints, palette: { primary }, spacing }) => ({
    stickyHeader: {
      padding: spacing(8, 0, 6, 0),
      ...sticky(primary.white, 105),
    },
    description: {
      marginBottom: spacing(6),
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    },
    filters: {
      display: 'flex',
      alignItems: 'center',
      marginRight: spacing(2),
    },
    searchWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    search: {
      width: 300,
      marginRight: spacing(2),
    },
    addLeaveButton: {
      width: 'max-content',
    },
    table: {
      '& .grid-table-header': {
        ...sticky(primary.bluish8, 243, 998),
      },
      '& .actions-cell': {
        overflow: 'visible',
        paddingLeft: 0,
        paddingTop: spacing(2),
        paddingBottom: spacing(2),
        paddingRight: spacing(1),

        [breakpoints.up('xLg')]: {
          paddingRight: spacing(3),
        },
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '20px',
    },
    icon: {
      cursor: 'default',
    },
    actionsWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    year: {
      minWidth: 100,
      width: 100,
      marginRight: spacing(2),
    },
    yearMenu: {
      zIndex: 1000,
    },
    manageRequestActionButtons: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 32px)',
      gridColumnGap: 8,
    },
    dotsMenu: {
      marginLeft: spacing(2),
    },
    form: {
      display: 'grid',
      gridColumnGap: 12,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr)) 85px',
      gridTemplateAreas: `
        "user user user"  
        "from to amount"
        "absence absence absence"
        "reason reason reason"   
        "description description description"
      `,
    },
    userField: {
      gridArea: 'user',
    },
    reasonField: {
      gridArea: 'reason',
    },
    absenceField: {
      gridArea: 'absence',
    },
    fromField: {
      gridArea: 'from',
    },
    toField: {
      gridArea: 'to',
    },
    amountField: {
      gridArea: 'amount',
    },
    descriptionField: {
      gridArea: 'description',
    },
    approvedBy: {
      display: 'flex',
      gridColumnGap: 8,
      alignItems: 'center',
    },
    approvedByUser: {
      cursor: 'pointer',
    },
    personIcon: {
      backgroundColor: primary.bluish7,
      '& img': { width: 18, height: 18 },
    },
    userActionWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    userAvatar: {
      marginRight: spacing(1),
    },
    userActionColumn: {
      paddingLeft: spacing(4),
      overflow: 'visible',
    },
  })
);

const AbsenceRequestsPage = ({
  location,
  auth,
  accessControl,
  navigate,
  dispatch,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ABSENCE_REQUESTS);
  const { page: defaultPage } = ABSENCE_REQUESTS_DEFAULT_PARAMS;
  const { PAGE, ORDERING, STATUS, SEARCH, YEAR, ABSENCE_REQUEST_DATES, USER } =
    PARAMS;
  const { ABSENCE, ABSENCE_FROM, ABSENCE_TO, DESCRIPTION, REASON } =
    ABSENCE_REQUEST_FIELD_NAMES;
  const { HAS_REASONS } = ABSENCE_FIELD_NAMES;

  const absenceRequests = useSelector(absenceRequestsSelector);
  const { absences } = useSelector(personAbsencesSelector);

  const yearOptions = prepareYearOptions();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState(INITIAL_ABSENCE_REQUESTS_SORT);
  const [selectedFilters, setSelectedFilters] = useState({
    [STATUS]: [],
  });
  const [allUsersWithAbsences, setAllUsersWithAbsences] = useState([]);
  const [absenceRequest, setAbsenceRequest] = useState(null);
  const [isManageRequestOpened, setIsManageRequestOpened] = useState(false);
  const [isDeleteRequestOpened, setIsDeleteRequestOpened] = useState(false);
  const [isApproveRequestOpened, setIsApproveRequestOpened] = useState(false);
  const [isRejectRequestOpened, setIsRejectRequestOpened] = useState(false);
  const [currentYear, setCurrentYear] = useState(yearOptions[1].value);
  const isFilterNextYear = currentYear === yearOptions[0]?.value;

  const isDoubleApprove = organizationSettings.absence_double_approval;
  const availableUserAbsences = prepareUserAbsences(absences);
  const statuses = prepareTranslatedAbsenceRequestStatuses(
    translations.statuses
  );
  const filterStatuses = prepareTranslatedAbsenceRequestStatuses(
    translations.statuses,
    true
  );
  const filters = prepareAbsenceRequestsPageFilters(
    translations,
    filterStatuses
  );
  const gridHeaderActions = prepareAbsenceRequestsPageTableActionsHeader(
    translations.columns
  );
  const gridHeaders = prepareAbsenceRequestsPageTableHeaders(
    translations.columns,
    classes
  );
  const isActiveFilter = hasSelectedFiltersCheck(selectedFilters);
  const hasLoadMore = hasNextPage(absenceRequests);
  const noResults = prepareNoResultsLabels(
    translations.noResults,
    isLoading,
    search || isActiveFilter
  );
  const manageRequestInitialData = prepareManageAbsenceRequestInitialData(
    absenceRequest,
    absenceRequest?.user?.id
  );

  const getAbsenceManagableUsers = async year => {
    const presentYear = +formatDate(new Date(), 'yyyy');
    const { data } = await http.get(API_ABSENCE_USERS_WITH_ABSENCES, {
      params: {
        [PARAMS.AT_ABSENCE_YEAR]: year,
        ...(presentYear === year ? { include_myself: true } : {}),
      },
    });
    setAllUsersWithAbsences(data);
  };

  const canResolveRequestCheck = useCallback(
    user =>
      hasScopedAuthPermissionCheck(
        PERMISSION_CODES.ABSENCE_REQUESTS_MANAGE,
        accessControl,
        auth.id,
        user.id
      ),
    [auth, accessControl]
  );

  const canAccessUserProfileCheck = useCallback(
    user => isUserProfileAccessibleCheck(accessControl, auth.id, user.id),
    [auth, accessControl]
  );

  const handleGetUserAbsences = userId =>
    getPersonAbsences(dispatch, userId, currentYear);

  const goToUserProfilePage = userId => navigate(`/people/${userId}`);

  const handlePageChange = ({
    newPage,
    newOrdering,
    newFilters,
    newSearch,
    newYear,
    isLoadMore,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      [ORDERING]: newOrdering?.sortKey,
      ...newFilters,
      ...(newSearch ? { [SEARCH]: newSearch } : {}),
      [YEAR]: newYear,
    };

    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/absence/requests/?${query}`, { replace: true });
    }

    return getAllAbsenceRequests(dispatch, isLoadMore, params);
  };

  const onChangeYear = selectedYear => {
    setPage(defaultPage);
    setCurrentYear(selectedYear);
    getAbsenceManagableUsers(selectedYear);
    handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newOrdering: ordering,
      newFilters: selectedFilters,
      newYear: selectedYear,
    });
  };

  const onSearch = async text => {
    const searchTerm = trimString(text);

    try {
      setPage(defaultPage);
      setSearch(searchTerm);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: searchTerm,
        newOrdering: ordering,
        newFilters: selectedFilters,
        newYear: currentYear,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSort = newOrdering => {
    setPage(defaultPage);
    setOrdering(newOrdering);
    handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newOrdering,
      newFilters: selectedFilters,
      newYear: currentYear,
    });
  };

  const onFiltersChange = async newFilters => {
    try {
      setIsLoading(true);
      setPage(defaultPage);
      setSelectedFilters(newFilters);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters,
        newYear: currentYear,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newOrdering: ordering,
      newFilters: selectedFilters,
      newYear: currentYear,
      isLoadMore: true,
    });
  };

  const handleOpenManageRequest = () => setIsManageRequestOpened(true);

  const handleOpenEditRequest = async currentRequest => {
    await getPersonAbsences(dispatch, currentRequest?.user?.id, currentYear);
    setIsManageRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseManageRequest = () => {
    setIsManageRequestOpened(false);
    setAbsenceRequest(null);
    dispatch(clearPersonAbsences());
  };

  const onSaveRequest = async values => {
    const isOwnRequest = values.user.id === auth.id;
    const hasManageOwnRequestPermissions = canResolveRequestCheck(values.user);
    const absenceRequestDates = prepareWorkdays(
      values[ABSENCE_FROM],
      values[ABSENCE_TO]
    );
    const data = {
      [ABSENCE]: values[ABSENCE].id,
      [DESCRIPTION]: values[ABSENCE][HAS_REASONS] ? '' : values[DESCRIPTION],
      [REASON]: values[ABSENCE][HAS_REASONS] ? values[REASON] : '',
      [ABSENCE_REQUEST_DATES]: absenceRequestDates,
      [USER]: values.user.id,
    };

    if (absenceRequest) {
      await http.patch(
        isOwnRequest && !hasManageOwnRequestPermissions
          ? api_absence_request_own(absenceRequest.id)
          : api_absence_request(absenceRequest.id),
        data
      );
      showSuccessMessage(
        translations.manageAbsenceRequest.successMessages.edit
      );
      setPage(defaultPage);
      return handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters: selectedFilters,
        newYear: currentYear,
      });
    }

    await http.post(
      isOwnRequest && !hasManageOwnRequestPermissions
        ? API_ABSENCE_REQUESTS_OWN
        : API_ABSENCE_REQUESTS,
      data
    );
    showSuccessMessage(translations.manageAbsenceRequest.successMessages.add);
    setPage(defaultPage);
    return handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newOrdering: ordering,
      newFilters: selectedFilters,
      newYear: currentYear,
    });
  };

  const handleOpenDeleteRequest = currentRequest => {
    setIsDeleteRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseDeleteRequest = () => {
    setIsDeleteRequestOpened(false);
  };

  const onDeleteRequest = async () => {
    try {
      const isOwnRequest = absenceRequest.user.id === auth.id;
      const hasManageOwnRequestPermissions = canResolveRequestCheck(
        absenceRequest.user
      );
      await http.delete(
        isOwnRequest && !hasManageOwnRequestPermissions
          ? api_absence_request_delete_own(absenceRequest.id)
          : api_absence_request(absenceRequest.id)
      );
      showSuccessMessage(translations.deleteRequestDialog.deleteSuccess);
      setPage(defaultPage);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters: selectedFilters,
        newYear: currentYear,
      });
    } finally {
      setIsDeleteRequestOpened(false);
      setAbsenceRequest(null);
      setIsManageRequestOpened(false);
    }
  };

  const handleOpenApproveRequest = currentRequest => () => {
    setIsApproveRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseApproveRequest = () => {
    setIsApproveRequestOpened(false);
    setAbsenceRequest(null);
  };

  const onApproveRequest = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequest.id), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.APPROVED,
      });
      showSuccessMessage(translations.approveRequestDialog.approveSuccess);
      setPage(defaultPage);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters: selectedFilters,
        newYear: currentYear,
      });
    } finally {
      handleCloseApproveRequest();
    }
  };

  const handleOpenRejectRequest = currentRequest => () => {
    setIsRejectRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseRejectRequest = () => {
    setIsRejectRequestOpened(false);
    setAbsenceRequest(null);
  };

  const onRejectRequest = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequest.id), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.REJECTED,
      });
      showSuccessMessage(translations.rejectRequestDialog.rejectSuccess);
      setPage(defaultPage);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters: selectedFilters,
        newYear: currentYear,
      });
    } finally {
      handleCloseRejectRequest();
    }
  };

  const handleUserStatus = async requestId => {
    return http.get(api_absence_request_actions(requestId));
  };

  const getInitialData = async () => {
    try {
      const params = parseQueryParams(
        location.search,
        PAGE_WHITELISTED_PARAMS.ABSENCE_REQUESTS
      );

      if (!isObjectEmpty(params)) {
        const [ascending, descending] = params[ORDERING].split('-');
        const isAscending = !!ascending;
        const initialOrdering = {
          column: getItemById(
            gridHeaders,
            isAscending ? ascending : descending,
            'sortAs'
          )?.rowKey,
          asc: isAscending,
          sortKey: params[ORDERING],
        };
        const initialSearch = params[SEARCH]
          ? decodeURIComponent(params[SEARCH])
          : '';
        const initialFilters = {
          [STATUS]: params[STATUS] ? [params[STATUS]] : [],
        };
        const initialYear = params[YEAR] || currentYear;

        setOrdering(initialOrdering);
        setSearch(initialSearch);
        setSelectedFilters(initialFilters);
        setCurrentYear(initialYear);
        await Promise.all([
          getAbsenceManagableUsers(initialYear),
          handlePageChange({
            newPage: page,
            newOrdering: initialOrdering,
            newFilters: initialFilters,
            newSearch: initialSearch,
            newYear: initialYear,
          }),
        ]);
      } else {
        await Promise.all([
          getAbsenceManagableUsers(currentYear),
          handlePageChange({
            newPage: page,
            newOrdering: ordering,
            newFilters: selectedFilters,
            newSearch: search,
            newYear: currentYear,
          }),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  };

  const cleanup = useCallback(() => {
    dispatch(clearAllAbsenceRequests());
    dispatch(clearPersonAbsences());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const renderUser = user => (
    <UserAvatar labelClass={classes.userLabel} user={user} caption small />
  );

  const renderStatus = currentStatus => (
    <StatusIndicator status={currentStatus} isDoubleApprove={isDoubleApprove} />
  );

  const renderAbsence = currentAbsence => (
    <Tooltip
      customIconClass={classes.icon}
      text={currentAbsence.name}
      icon={currentAbsence.icon}
    />
  );

  const renderActions = (
    request,
    hasManageRequestButtons,
    hasEdit,
    hasDelete
  ) => {
    const menuItems = prepareAbsenceRequestMenuItems(
      translations.menuItems,
      request,
      hasEdit,
      hasDelete,
      false,
      handleOpenEditRequest,
      handleOpenDeleteRequest,
      undefined
    );

    return (
      <div className={classes.actionsWrapper}>
        {hasManageRequestButtons && (
          <div className={classes.manageRequestActionButtons}>
            <ActionButton
              type={ACTION_BUTTON_TYPES.CLOSE}
              tooltipText={translations.menuItems.reject}
              onClickHandler={handleOpenRejectRequest(request)}
              isSquared
            />
            <ActionButton
              type={ACTION_BUTTON_TYPES.ACCEPT}
              tooltipText={translations.menuItems.approve}
              onClickHandler={handleOpenApproveRequest(request)}
              isSquared
            />
          </div>
        )}
        {(hasEdit || hasDelete) && (
          <DotsMenu
            className={classes.dotsMenu}
            menuItems={menuItems}
            isVerticalMenu
          />
        )}
      </div>
    );
  };

  const renderUserAction = (user, isAccessible) => {
    const status = user.action ? getItemById(statuses, user.action) : '';

    return (
      <div className={classes.userActionWrapper}>
        <UserAvatar
          className={classes.userAvatar}
          variant="subtitle1"
          clickableCaption={isAccessible}
          user={user}
          small
          caption
        />
        {status && status.id !== ABSENCE_REQUEST_STATUS_TYPES.REQUESTED && (
          <StatusIndicator status={status} />
        )}
      </div>
    );
  };

  const renderApprovedByUsers = request => {
    const hasAdminAction = request.admin;
    const hasReportToAction = request.report_to;
    const isRequestApprovedOrRejected = [
      ABSENCE_REQUEST_STATUS_TYPES.APPROVED,
      ABSENCE_REQUEST_STATUS_TYPES.REJECTED,
    ].includes(request.status);
    if (
      (isRequestApprovedOrRejected && !hasAdminAction && !hasReportToAction) ||
      !request.used_days
    ) {
      return '-';
    }

    const adminWithAction = { ...request.admin, action: request.admin_action };
    const reportToWithAction = {
      ...request.report_to,
      action: request.report_to_action,
    };

    const userWithAction = hasAdminAction
      ? adminWithAction
      : reportToWithAction;
    const approvedBy =
      (isDoubleApprove &&
        !(
          isRequestApprovedOrRejected &&
          ((hasAdminAction && !hasReportToAction) ||
            (!hasAdminAction && hasReportToAction))
        )) ||
      (!isDoubleApprove && hasReportToAction)
        ? [adminWithAction, reportToWithAction]
        : [userWithAction];

    return (
      <div className={classes.approvedBy}>
        {approvedBy.map((approvedByUser, index) => {
          return approvedByUser?.id ? (
            <AsyncListTooltip
              key={`approved_by_${index}`}
              labelData={[approvedByUser]}
              renderLabelItem={renderUserAction}
              isItemClickable={canAccessUserProfileCheck}
              onItemClickHandler={goToUserProfilePage}
            >
              <UserAvatar
                user={approvedByUser}
                className={classes.approvedByUser}
                small
              />
            </AsyncListTooltip>
          ) : (
            <AsyncListTooltip
              key={`approved_by_${index}`}
              getLabelData={() => handleUserStatus(request.id)}
              renderLabelItem={renderUserAction}
              isItemClickable={canAccessUserProfileCheck}
              onItemClickHandler={goToUserProfilePage}
            >
              <Avatar
                avatarPicture={PersonIcon}
                avatarText=""
                customAvatarClass={classNames(
                  classes.approvedByUser,
                  classes.personIcon
                )}
                small
              />
            </AsyncListTooltip>
          );
        })}
      </div>
    );
  };

  const manageRequestFields = prepareManageAbsenceRequestFields(
    classes,
    currentYear,
    !!absenceRequest,
    true,
    handleGetUserAbsences
  );

  const absenceRequestsTableData = prepareAbsenceRequestsTableData(
    translations,
    auth,
    canResolveRequestCheck,
    isDoubleApprove,
    absenceRequests?.results,
    statuses,
    renderUser,
    renderStatus,
    renderAbsence,
    renderActions,
    renderApprovedByUsers
  );

  return (
    <PageContainer
      {...rest}
      translations={translations}
      location={location}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div className={classes.description}>
              <Typography variant="body2">
                {translations.description}
              </Typography>
            </div>
            <div className={classes.actions}>
              <div className={classes.filters}>
                <Filters
                  translations={translations.filters}
                  selectedFilters={selectedFilters}
                  filters={filters}
                  onApplyFilters={onFiltersChange}
                />
              </div>
              <div className={classes.searchWrapper}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={onSearch}
                />
                <SelectField
                  className={classes.year}
                  menuClass={classes.yearMenu}
                  value={currentYear}
                  options={yearOptions}
                  onChange={onChangeYear}
                  isSearchDisabled
                  shouldRemoveLabel
                />
                <CustomButton
                  className={classes.addLeaveButton}
                  type="addRoundedNew"
                  onClick={handleOpenManageRequest}
                  disabled={isFilterNextYear}
                >
                  {translations.addLeaveButtonLabel}
                </CustomButton>
              </div>
            </div>
          </div>
          {isArray(absenceRequests.results) &&
            !isArrayEmpty(absenceRequests.results) && (
              <GridTable
                className={classes.table}
                translations={translations}
                initialSort={ordering}
                headerActions={gridHeaderActions}
                headers={gridHeaders}
                hasLoadMore={hasLoadMore}
                rows={absenceRequestsTableData}
                onLoadMore={onLoadMore}
                onSort={onSort}
              />
            )}
          <NotificationCard
            title={noResults.title}
            content={noResults.content}
            shouldFade={
              !isLoading &&
              isArray(absenceRequests?.results) &&
              isArrayEmpty(absenceRequests.results)
            }
          />
          <CustomFormDrawer
            customFormClass={classes.form}
            translations={translations.manageAbsenceRequest}
            isOpened={isManageRequestOpened}
            initialData={manageRequestInitialData}
            isInitialValid={!!absenceRequest}
            fields={manageRequestFields}
            absences={availableUserAbsences}
            allUsers={allUsersWithAbsences}
            onDelete={handleOpenDeleteRequest}
            onClose={handleCloseManageRequest}
            onSave={onSaveRequest}
            hasCancelButton
          />
          <AlertDialog
            translations={translations.deleteRequestDialog}
            isOpened={isDeleteRequestOpened}
            onClose={handleCloseDeleteRequest}
            onConfirm={onDeleteRequest}
            isWarning
          />
          <AlertDialog
            translations={translations.approveRequestDialog}
            isOpened={isApproveRequestOpened}
            onClose={handleCloseApproveRequest}
            onConfirm={onApproveRequest}
            isWarning
          />
          <AlertDialog
            translations={translations.rejectRequestDialog}
            isOpened={isRejectRequestOpened}
            onClose={handleCloseRejectRequest}
            onConfirm={onRejectRequest}
            isWarning
          />
        </div>
      )}
    </PageContainer>
  );
};

AbsenceRequestsPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(AbsenceRequestsPage);
